import React, { useState, useEffect, useRef } from 'react';
import { calendar } from '../../api';
import CalendarEventList from '../Calendar/CalendarEventList';

const REFRESH_MS = 60 * 1000;

const dayString = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));
const sameDay = (a, b) => dayString(a) === dayString(b);

// Headcoach: pick one of your coaches and see the iPhone calendar they linked - one day at a time, or a week at a glance
// (tap the date to open the week, tap a day in it to go back to that day). Re-reads every minute while it is open.
const CoachCalendarsCard = ({ alwaysShow = false }) => {
  const [coaches, setCoaches] = useState(null);
  const [coachId, setCoachId] = useState('');
  const [date, setDate] = useState(() => new Date());
  const [view, setView] = useState('day'); // 'day' | 'week'
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [weekCounts, setWeekCounts] = useState({}); // 'YYYY-MM-DD' -> number of events
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const shownKey = useRef('');

  useEffect(() => {
    calendar.getDeviceCoaches()
      .then(res => {
        const list = res.data.coaches || [];
        setCoaches(list);
        const first = list.find(c => c.iphoneCalendarConnected) || list[0];
        if (first) setCoachId(first._id);
      })
      .catch(() => { setCoaches([]); setError('Could not load your coaches'); });
  }, []);

  // Every minute, while the screen is visible, ask again
  useEffect(() => {
    const timer = setInterval(() => { if (!document.hidden) setTick(t => t + 1); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  // The selected day
  useEffect(() => {
    if (!coachId) return undefined;
    let cancelled = false;
    const key = `${coachId}|${dayString(date)}`;
    const changed = shownKey.current !== key;
    if (changed) { setLoading(true); setData(null); }
    setError('');
    calendar.getDeviceCoachEvents(coachId, dayString(date))
      .then(res => { if (!cancelled) { setData(res.data); shownKey.current = key; } })
      .catch(() => { if (!cancelled && changed) { setData(null); setError('Could not load that calendar'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [coachId, date, tick]);

  // The week: one request per day, counted
  useEffect(() => {
    if (view !== 'week' || !coachId) return undefined;
    let cancelled = false;
    const days = Array.from({ length: 7 }, (_, i) => dayString(addDays(weekStart, i)));
    Promise.all(days.map(d => calendar.getDeviceCoachEvents(coachId, d).then(res => [d, (res.data.events || []).length]).catch(() => [d, null])))
      .then(pairs => { if (!cancelled) setWeekCounts(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [view, coachId, weekStart, tick]);

  const shift = (days) => setDate(prev => addDays(prev, days));
  const openWeek = () => { setWeekStart(mondayOf(date)); setWeekCounts({}); setView('week'); };
  const pickDay = (d) => { setDate(d); setView('day'); };
  const changeCoach = (id) => { setCoachId(id); setWeekCounts({}); };

  if (coaches !== null && coaches.length === 0 && !error && !alwaysShow) return null;

  const coachNotLinked = data && !data.calendarConnected;
  const selectedCoach = (coaches || []).find(c => c._id === coachId);
  const today = new Date();

  return (
    <div className="settings-section">
      <label>Coach Calendars</label>
      {alwaysShow && coaches !== null && coaches.length === 0 && !error && (
        <p className="password-info">No coaches are assigned to you yet.</p>
      )}
      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <p>{error}</p>
        </div>
      )}
      {coaches && coaches.length > 0 && (
        <>
          <select value={coachId} onChange={(e) => changeCoach(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '10px' }}>
            {coaches.map(c => (
              <option key={c._id} value={c._id}>{c.name}{c.iphoneCalendarConnected ? '' : ' (iPhone calendar not linked)'}</option>
            ))}
          </select>

          {view === 'week' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                <button type="button" className="cancel-button" onClick={() => { setWeekStart(addDays(weekStart, -7)); setWeekCounts({}); }}>←</button>
                <strong>
                  {weekStart.toLocaleDateString([], { day: 'numeric', month: 'short' })} – {addDays(weekStart, 6).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                </strong>
                <button type="button" className="cancel-button" onClick={() => { setWeekStart(addDays(weekStart, 7)); setWeekCounts({}); }}>→</button>
              </div>
              {selectedCoach && !selectedCoach.iphoneCalendarConnected && (
                <p className="password-info">This coach hasn't linked an iPhone calendar. Coaches with Google Calendar keep using that one.</p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                {Array.from({ length: 7 }, (_, i) => {
                  const d = addDays(weekStart, i);
                  const count = weekCounts[dayString(d)];
                  const isToday = sameDay(d, today);
                  const isSelected = sameDay(d, date);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => pickDay(d)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                        padding: '10px 2px', borderRadius: '10px', cursor: 'pointer', minWidth: 0,
                        border: isSelected ? '2px solid #2f4ba2' : '1px solid #e0e0e0',
                        background: isToday ? '#e8edfb' : 'white', color: '#222'
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 600 }}>{d.toLocaleDateString([], { weekday: 'short' })}</span>
                      <span style={{ fontSize: '16px', fontWeight: 700 }}>{d.getDate()}</span>
                      <span style={{
                        minWidth: '22px', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                        background: count > 0 ? '#2f4ba2' : '#f0f0f0', color: count > 0 ? 'white' : '#999'
                      }}>
                        {count == null ? '–' : count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="password-info" style={{ marginTop: '10px' }}>The number under each day is how many lessons and events are in that day. Tap a day to see them. Updates every minute.</p>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                <button type="button" className="cancel-button" onClick={() => shift(-1)}>←</button>
                <button
                  type="button"
                  onClick={openWeek}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: 700, color: '#2f4ba2', padding: '6px 10px' }}
                  aria-label="Open the week view"
                >
                  {date.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })} 🗓️
                </button>
                <button type="button" className="cancel-button" onClick={() => shift(1)}>→</button>
              </div>
              {loading ? (
                <p className="password-info">Loading...</p>
              ) : coachNotLinked ? (
                <p className="password-info">This coach hasn't linked an iPhone calendar. Coaches with Google Calendar keep using that one.</p>
              ) : (
                <CalendarEventList events={data?.events} syncedAt={data?.syncedAt} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CoachCalendarsCard;
